
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getErrorMessage, supabase } from '../services/supabaseClient';
import { ApiService } from '../services/api';
import { DashboardMetrics, FleetTrialState, RecentActivityItem } from '../types';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  PoundSterling,
  Car,
  Loader2,
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  User,
  Briefcase,
  Scale,
  Zap,
  Crosshair,
  Target,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { WelcomeWizard } from '../components/WelcomeWizard';

const DASHBOARD_REFRESH_INTERVAL_MS = 60_000;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'month' | 'quarter'>('month');

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);
  const [offenderTab, setOffenderTab] = useState<'drivers' | 'vehicles'>('drivers');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [trialState, setTrialState] = useState<FleetTrialState | null>(null);

  // Welcome Wizard State
  const [showWelcomeWizard, setShowWelcomeWizard] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Radar Animation State
  const [radarRotation, setRadarRotation] = useState(0);
  const inFlight = useRef(false);

  const fetchDashboardData = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (inFlight.current) return;
    inFlight.current = true;

    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setError(null);
      }

      const [data, activity] = await Promise.all([
        ApiService.getDashboardData(period),
        ApiService.getRecentActivity(15)
      ]);
      setMetrics(data);
      setRecentActivity(activity);
      setLastUpdatedAt(new Date().toISOString());

      try {
        const state = await ApiService.getFleetTrialState();
        setTrialState(state);
      } catch (orgErr) {
        console.warn('Unable to load trial state:', orgErr);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      const msg = getErrorMessage(err);

      // Check for invalid JWT/session errors
      if (msg.includes('Session expired') ||
          msg.includes('invalid claim') ||
          msg.includes('missing sub claim') ||
          msg === 'User not authenticated') {
        console.warn('Invalid session detected in Dashboard, redirecting to login...');
        navigate('/login');
        return;
      }

      if (!silent) setError(msg);
      addToast(silent ? `Dashboard refresh failed: ${msg}` : msg, 'error');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    // Initial load & whenever period changes
    void fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    // Simple rotation loop for radar
    const interval = setInterval(() => {
      setRadarRotation(prev => (prev + 2) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Auto-refresh to keep dashboard up to date
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void fetchDashboardData({ silent: true });
    }, DASHBOARD_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    // Check if Welcome Wizard needs to be shown
    const checkWelcomeWizard = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Check if wizard already completed
        const { data: prefs } = await supabase
          .from('user_preferences')
          .select('preferences')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        const completed = prefs?.preferences?.welcome_wizard_completed;

        if (!completed) {
          // Get organization ID
          const { data: profile } = await supabase
            .from('profiles')
            .select('organisation_id')
            .eq('id', user.id)
            .limit(1)
            .maybeSingle();

          if (profile?.organisation_id) {
            setOrganizationId(profile.organisation_id);
            setShowWelcomeWizard(true);
          }
        }
      } catch (err) {
        console.error('Error checking welcome wizard status:', err);
      }
    };

    checkWelcomeWizard();
  }, []);

  const forceOnboarding = new URLSearchParams(location.search).get('onboarding') === '1';

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl border border-red-200 dark:border-red-900 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium">Error loading dashboard</h3>
          <p className="text-sm mt-1 break-words font-mono text-xs opacity-90">{error}</p>
          <button 
            onClick={() => {
              void fetchDashboardData();
            }}
            className="mt-3 text-sm font-medium underline hover:text-red-800 dark:hover:text-red-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // --- Radar Helper ---
  const formatRelativeTime = (iso: string) => {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const diffSec = Math.floor((Date.now() - t) / 1000);
    if (diffSec < 30) return 'Just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  const getActivityPresentation = (kind: RecentActivityItem['kind']) => {
    switch (kind) {
      case 'transfer':
        return {
          icon: <Briefcase size={14} />,
          color: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400'
        };
      case 'appeal':
        return {
          icon: <AlertTriangle size={14} />,
          color: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
        };
      case 'upload':
        return {
          icon: <TrendingUp size={14} />,
          color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400'
        };
      case 'payment':
        return {
          icon: <PoundSterling size={14} />,
          color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400'
        };
      case 'ticket':
        return {
          icon: <CheckCircle2 size={14} />,
          color: 'text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-300'
        };
      default:
        return {
          icon: <Zap size={14} />,
          color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400'
        };
    }
  };

  const radarHotspots = metrics.hotspots.slice(0, 5).map((h, i) => {
    const angle = (i * 72) - 90;
    const radian = (angle * Math.PI) / 180;
    const distance = 35 + (i % 2) * 10; 
    const x = 50 + distance * Math.cos(radian);
    const y = 50 + distance * Math.sin(radian);
    return { ...h, x, y };
  });
  const trialCommercialStatus = String(trialState?.commercial_status || '');
  const trialSubscriptionTier = String(trialState?.subscription_tier || '');
  const isPaidFleetModeOrg =
    trialCommercialStatus === 'paid' ||
    ['growth', 'pro_fleet', 'concierge', 'pro', 'managed'].includes(trialSubscriptionTier);
  const isTrialWorkspace = Boolean(
    trialState &&
    !isPaidFleetModeOrg &&
    (
      trialState.trial_status === 'active' ||
      trialState.trial_status === 'expired' ||
      ['trial', 'trial_complete', 'qualified_trial', 'no_buy', 'nurture'].includes(trialCommercialStatus)
    )
  );
  const trialProcessed = Math.min(trialState?.trial_pcns_processed ?? 0, trialState?.trial_pcn_limit ?? 3);
  const trialLimit = trialState?.trial_pcn_limit ?? 3;
  const trialEndsAtMs = trialState?.trial_expires_at ? new Date(trialState.trial_expires_at).getTime() : null;
  const trialExpiredByTime = typeof trialEndsAtMs === 'number' && Number.isFinite(trialEndsAtMs) && trialEndsAtMs <= Date.now();
  const trialComplete = trialProcessed >= trialLimit || trialExpiredByTime || trialState?.trial_status === 'expired' || Boolean(trialState?.locked);
  const trialEnds = trialState?.trial_expires_at ? new Date(trialState.trial_expires_at).toLocaleDateString('en-GB') : '7 days after signup';
  const trialLockedByLimit = trialState?.expired_by === 'pcn_limit' || trialProcessed >= trialLimit;
  const trialLockedByTime = trialState?.expired_by === 'time' || (trialExpiredByTime && trialProcessed < trialLimit);
  const primaryTrialAction = trialLockedByLimit ? 'Open snapshot' : trialLockedByTime ? 'Choose a plan' : trialProcessed === 0 ? 'Upload first PCN' : 'Upload next PCN';
  const handlePrimaryTrialAction = () => {
    if (trialLockedByLimit) navigate('/trial-report');
    else if (trialLockedByTime) navigate('/pricing');
    else navigate('/tickets?upload=1');
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Welcome Wizard Modal */}
      {showWelcomeWizard && organizationId && (
        <WelcomeWizard
          organizationId={organizationId}
          onComplete={() => {
            setShowWelcomeWizard(false);
            window.location.reload(); // Reload to apply new business model
          }}
        />
      )}

      <OnboardingChecklist forceOpen={forceOnboarding} />

      {isTrialWorkspace && (
        <section className="rounded-lg border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-900/50 dark:bg-slate-900">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/30 dark:text-primary-300">
                <Zap size={14} />
                3-PCN AI Control Trial
              </div>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                {trialLockedByTime
                  ? 'Your FleetMode trial has ended. Your workspace data is still available.'
                  : trialLockedByLimit
                    ? 'Your 3-PCN trial snapshot is ready.'
                    : 'Start with one processed PCN, then build your 3-PCN snapshot.'}
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {trialLockedByTime
                  ? 'Uploads and manual ticket creation are paused until the organisation chooses a plan or restarts with the FleetMode team. Existing PCNs, reports, and notes remain visible.'
                  : trialLockedByLimit
                    ? 'Uploads are paused at the 3-PCN limit. Review the snapshot, choose a plan, or request a manually reviewed 10-PCN savings report.'
                    : 'Processed means AI extraction is complete and your team has confirmed or accepted the key fields. The five AI assistant panels are outputs for one PCN, not extra processed PCNs.'}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={handlePrimaryTrialAction}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {primaryTrialAction}
                  <ArrowRight size={16} />
                </button>
                {trialComplete && (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                  >
                    View plans
                    <ArrowRight size={16} />
                  </button>
                )}
                <button
                  onClick={() => navigate('/vehicles')}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Add vehicle details
                </button>
                <button
                  onClick={() => navigate('/hires')}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Add hire / lease agreement
                </button>
                {trialProcessed > 0 && (
                  <button
                    onClick={() => navigate('/trial-report')}
                    className="inline-flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-bold text-primary-700 transition hover:bg-primary-100 dark:border-primary-900/60 dark:bg-primary-950/30 dark:text-primary-300 dark:hover:bg-primary-950/50"
                  >
                    View trial report
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950/40 lg:max-w-xs">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">PCNs processed</p>
                  <p className="mt-1 text-4xl font-bold text-slate-950 dark:text-white">{trialProcessed}/{trialLimit}</p>
                </div>
                <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                  Trial ends<br />
                  <span className="font-bold text-slate-800 dark:text-slate-200">{trialEnds}</span>
                </div>
              </div>
              <div className="mt-4 h-2 rounded-full bg-slate-200 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-primary-600"
                  style={{ width: `${Math.min(100, (trialProcessed / trialLimit) * 100)}%` }}
                />
              </div>
              {trialComplete && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                  {trialLockedByTime
                    ? 'This workspace is now on a no-buy nurture path. Data is preserved, and processing can resume after plan selection or team review.'
                    : 'The 3-PCN sample is complete. The FleetMode team can manually approve a 10-PCN extension during a conversion review.'}
                  <button
                    onClick={() => navigate(trialLockedByTime ? '/pricing' : '/trial-report')}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-900 px-3 py-2 text-xs font-bold text-white hover:bg-amber-950"
                  >
                    {trialLockedByTime ? 'Choose plan' : 'Open snapshot'}
                    <ArrowRight size={14} />
                  </button>
                </div>
              )}
              {trialState?.trial_extension_status === 'unlocked' && (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs font-semibold text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                  10-PCN savings report unlocked.
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Fleet Overview</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Analytics for {period === 'month' ? 'the last 30 days' : 'this quarter (90 days)'}.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => fetchDashboardData({ silent: true })}
              disabled={refreshing}
              className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-60"
              title="Refresh dashboard"
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
            <div className="flex bg-white dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <button 
                onClick={() => setPeriod('month')}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${period === 'month' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
              >
                This Month
              </button>
              <button 
                onClick={() => setPeriod('quarter')}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${period === 'quarter' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
              >
                This Quarter
              </button>
            </div>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 text-right">
            {lastUpdatedAt ? `Last updated ${new Date(lastUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—'}
            <span className="ml-2">Auto-refresh: 60s</span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Total Volume */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 transition-all hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Tickets</h3>
            <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{metrics.totalTickets}</p>
            <span className="text-xs text-slate-400 mb-1.5 font-medium">in period</span>
          </div>
        </div>

        {/* Money At Risk */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 border-l-4 border-l-amber-500 dark:border-l-amber-500 transition-all hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">Money at Risk</h3>
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
              <AlertCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <p className="text-3xl font-bold text-slate-900 dark:text-white">£{metrics.financials.atRisk.toLocaleString()}</p>
            <span className="text-xs text-amber-600 dark:text-amber-400 mb-1.5 font-bold">Outstanding</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">Open tickets + Pending Appeals</p>
        </div>

        {/* Money Paid */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 border-l-4 border-l-green-500 dark:border-l-green-500 transition-all hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Paid</h3>
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <p className="text-3xl font-bold text-slate-900 dark:text-white">£{metrics.financials.paid.toLocaleString()}</p>
            <span className="text-xs text-green-600 dark:text-green-400 mb-1.5 font-bold">Settled</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">Paid directly by company</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">Overdue Vehicle Docs</h3>
            <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.compliance.vehiclesOverdueDocs}</p>
          <p className="text-xs text-slate-400 mt-1">MOT, Tax, Insurance overdue</p>
        </div>

        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">Licence Risk Drivers</h3>
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
              <User className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.compliance.driversLicenceRisk}</p>
          <p className="text-xs text-slate-400 mt-1">Expiry within 30 days or invalid status</p>
        </div>

        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">Critical Breaches Open</h3>
            <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
              <Target className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.compliance.criticalBreachesOpen}</p>
          <p className="text-xs text-slate-400 mt-1">Defect and compliance items requiring urgent action</p>
        </div>
      </div>

      {/* Row 2: Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left: Status Funnel */}
        <div className="lg:col-span-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col">
          <h3 className="font-bold text-slate-900 dark:text-white mb-6 text-lg">Status Breakdown</h3>
          <div className="space-y-6 flex-1 flex flex-col justify-center">
            
            <div className="flex items-center justify-between group cursor-pointer p-2 -mx-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors" onClick={() => navigate('/tickets?status=active')}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center shadow-sm">
                   <Clock size={20} />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-xl">{metrics.byStage.open}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wide">Open</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-slate-300 dark:text-slate-600 group-hover:text-primary-600" />
            </div>

            <div className="border-t border-slate-100 dark:border-slate-800"></div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 flex items-center justify-center shadow-sm">
                   <Scale size={20} />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-xl">{metrics.byStage.appealed}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wide">In Appeal</p>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 dark:border-slate-800"></div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-sm">
                   <Briefcase size={20} />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-xl">{metrics.byStage.transferred}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wide">Transferred</p>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 dark:border-slate-800"></div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 flex items-center justify-center shadow-sm">
                   <PoundSterling size={20} />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-xl">{metrics.byStage.paid}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wide">Paid</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center: Live Threat Radar */}
        <div className="lg:col-span-4 bg-slate-900 dark:bg-black/40 p-0 rounded-2xl shadow-xl border border-slate-800 flex flex-col overflow-hidden relative group">
           {/* Radar Background & Grid */}
           <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
	             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] border border-primary-500 rounded-full"></div>
	             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] border border-primary-500 rounded-full"></div>
	             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] border border-primary-500 rounded-full"></div>
	             <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-full bg-primary-500/50"></div>
	             <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-[1px] bg-primary-500/50"></div>
	           </div>

           {/* Radar Sweep */}
	           <div 
	             className="absolute top-[5%] left-[5%] w-[90%] h-[90%] rounded-full z-0 pointer-events-none origin-center transition-transform duration-75"
	             style={{
	               background: 'conic-gradient(from 0deg, transparent 0deg, transparent 270deg, rgb(var(--color-primary-500) / 0.4) 360deg)',
	               transform: `rotate(${radarRotation}deg)`
	             }}
	           ></div>

           <div className="p-5 border-b border-slate-800 bg-slate-950/80 backdrop-blur z-10 flex items-center justify-between">
             <div className="flex items-center gap-2">
                <Crosshair size={20} className="text-red-500 animate-pulse" />
                <h3 className="font-bold text-white tracking-wide">LIVE THREAT RADAR</h3>
             </div>
	             <span className="text-[10px] text-primary-400 font-mono uppercase tracking-widest animate-pulse">Scanning...</span>
	           </div>
           
           <div className="flex-1 relative z-10 p-6 flex flex-col justify-end">
             {/* Blips on Map */}
             {radarHotspots.map((h, i) => (
                <div 
                  key={i}
                  className="absolute w-3 h-3 bg-red-500 rounded-full shadow-[0_0_15px_rgba(239,68,68,1)] animate-pulse cursor-pointer hover:scale-150 transition-transform"
                  style={{ top: `${h.y}%`, left: `${h.x}%` }}
                  title={`${h.name}: ${h.count} tickets`}
                >
                  <div className="absolute -inset-4 border border-red-500/30 rounded-full animate-ping"></div>
                </div>
             ))}

             {/* Legend List */}
             <div className="bg-slate-900/80 rounded-xl p-4 border border-slate-800 mt-auto backdrop-blur-sm shadow-lg">
                <p className="text-[10px] uppercase font-bold text-slate-500 mb-3">Top Hotspots</p>
                <div className="space-y-3">
                  {metrics.hotspots.slice(0, 3).map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <span className="text-red-500 font-mono font-bold">0{i + 1}</span>
                        <span className="text-slate-200 truncate max-w-[120px] font-medium">{h.name}</span>
                      </div>
	                      <span className="text-primary-400 font-mono">{h.count} PCNs</span>
	                    </div>
	                  ))}
	                </div>
	             </div>
           </div>
        </div>

	        {/* Right: Recent Activity Feed */}
	        <div className="lg:col-span-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col">
	          <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
	             <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
	               <Zap size={18} className="text-amber-500" /> Recent Activity
	             </h3>
	             <button onClick={() => navigate('/tickets')} className="text-xs text-primary-600 dark:text-primary-400 font-bold hover:underline">View All</button>
	          </div>
	          <div className="flex-1 overflow-y-auto p-0 max-h-[350px] scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
	            {recentActivity.length > 0 ? (
	              recentActivity.map((item) => {
	                const p = getActivityPresentation(item.kind);
	                const clickable = !!item.ticketId;
	                return (
	                  <div
	                    key={item.id}
	                    className={`flex gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50 last:border-0 ${
	                      clickable ? 'cursor-pointer' : ''
	                    }`}
	                    onClick={() => {
	                      if (!item.ticketId) return;
	                      navigate(`/tickets/${item.ticketId}`);
	                    }}
	                  >
	                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${p.color}`}>
	                      {p.icon}
	                    </div>
	                    <div className="min-w-0">
	                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={item.action}>
	                        {item.action}
	                      </p>
	                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate" title={item.desc}>
	                        {item.desc}
	                      </p>
	                      <p
	                        className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 font-medium"
	                        title={new Date(item.at).toLocaleString('en-GB')}
	                      >
	                        {formatRelativeTime(item.at)}
	                      </p>
	                    </div>
	                  </div>
	                );
	              })
	            ) : (
	              <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">No recent activity yet.</div>
	            )}
	          </div>
	        </div>
      </div>

      {/* Row 3: Top Offenders */}
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
           <h3 className="font-bold text-slate-900 dark:text-white text-lg flex items-center gap-2">
             <Target size={20} className="text-red-500" />
             Top Offenders
           </h3>
           <div className="flex bg-slate-100 dark:bg-slate-950 p-1 rounded-lg">
             <button 
               onClick={() => setOffenderTab('drivers')} 
               className={`px-3 py-1 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${offenderTab === 'drivers' ? 'bg-white dark:bg-slate-800 shadow text-slate-800 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}
             >
               <User size={14} /> Drivers
             </button>
             <button 
               onClick={() => setOffenderTab('vehicles')} 
               className={`px-3 py-1 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${offenderTab === 'vehicles' ? 'bg-white dark:bg-slate-800 shadow text-slate-800 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}
             >
               <Car size={14} /> Vehicles
             </button>
           </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800">
                <th className="px-6 py-4 font-semibold text-slate-500 dark:text-slate-400 w-20">Rank</th>
                <th className="px-6 py-4 font-semibold text-slate-500 dark:text-slate-400">
                  {offenderTab === 'drivers' ? 'Driver Name' : 'Vehicle VRM'}
                </th>
                <th className="px-6 py-4 font-semibold text-slate-500 dark:text-slate-400 text-right">Tickets</th>
                {offenderTab === 'drivers' && <th className="px-6 py-4 font-semibold text-slate-500 dark:text-slate-400 text-right">Liability Est.</th>}
                <th className="px-6 py-4 font-semibold text-slate-500 dark:text-slate-400 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
              {offenderTab === 'drivers' ? (
                metrics.topDrivers.length > 0 ? metrics.topDrivers.map((driver, index) => (
                  <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                     <td className="px-6 py-4 font-bold text-slate-400 dark:text-slate-600">#{index + 1}</td>
                     <td className="px-6 py-4 font-bold text-slate-800 dark:text-slate-200">{driver.name}</td>
                     <td className="px-6 py-4 text-right font-bold text-slate-900 dark:text-white">{driver.count}</td>
                     <td className="px-6 py-4 text-right text-slate-600 dark:text-slate-400">£{driver.cost.toLocaleString()}</td>
                     <td className="px-6 py-4 text-right">
                       <button onClick={() => navigate(`/tickets?driver=${driver.name}`)} className="text-primary-600 dark:text-primary-400 hover:underline text-xs font-bold uppercase tracking-wide">View History</button>
                     </td>
                  </tr>
                )) : <tr><td colSpan={5} className="p-8 text-center text-slate-400">No driver data available.</td></tr>
              ) : (
                metrics.topVehicles.length > 0 ? metrics.topVehicles.map((vehicle, index) => (
                  <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                     <td className="px-6 py-4 font-bold text-slate-400 dark:text-slate-600">#{index + 1}</td>
                     <td className="px-6 py-4 font-mono font-bold text-slate-700 dark:text-slate-300">{vehicle.vrn}</td>
                     <td className="px-6 py-4 text-right font-bold text-slate-900 dark:text-white">{vehicle.count}</td>
                     <td className="px-6 py-4 text-right">
                       <button onClick={() => navigate(`/tickets?search=${vehicle.vrn}`)} className="text-primary-600 dark:text-primary-400 hover:underline text-xs font-bold uppercase tracking-wide">View History</button>
                     </td>
                  </tr>
                )) : <tr><td colSpan={4} className="p-8 text-center text-slate-400">No vehicle data available.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
